import { z } from 'zod';

/**
 * IMPORTANT!!! PLEASE READ!!!
 *
 * This file exposes runtime environment variables as feature flags to
 * our client-side code. Do not put anything sensitive in this file
 * since it is being sent to the client!
 *
 * Add a feature flag if:
 * 1. You need access to an environment variable in client-side components.
 * 2. Your environment variable value varies across our local, dev, stage, and prod environment.
 *
 * You might not need to add a value here. See examples below.
 * 1. If you only need access to the value on the server, read it from server.ts instead.
 * 2. If this value is set at build time, use NEXT_PUBLIC_ environment variables as documented by Next.js.
 */

// Edit this object to define the parsing logic for your new feature flag
const featureFlagsSchema = z.object({
  PAGE_BANNER: z.preprocess((s) => {
    if (typeof s === 'string' && s === 'null') {
      return undefined;
    }
    return s;
  }, z.string().optional()),
  ALLOW_IMMEDIATE_UPLOAD: z
    .string()
    .optional()
    .default('false')
    .transform((s) => s === 'true'),
  DISABLE_DEADLINE_BANNERS: z
    .string()
    .optional()
    .default('false')
    .transform((s) => s === 'true'),
});

type FeatureFlags = z.infer<typeof featureFlagsSchema>;

// Edit this function to read your feature flag from process.env
function readProcessEnv(): FeatureFlags {
  return featureFlagsSchema.parse({
    PAGE_BANNER: process.env.PAGE_BANNER,
    ALLOW_IMMEDIATE_UPLOAD: process.env.ALLOW_IMMEDIATE_UPLOAD,
    DISABLE_DEADLINE_BANNERS: process.env.DISABLE_DEADLINE_BANNERS,
  });
}

// Call this function to access your feature flag in a component (client or server!)
export function getFeatureFlag<T extends keyof FeatureFlags>(flag: T): FeatureFlags[T] {
  return loadFeatureFlags()[flag];
}

/*
 * Implementation details below here.
 */

// We cache feature flags in memory once loaded.
let featureFlags: FeatureFlags | undefined;
function loadFeatureFlags(): FeatureFlags {
  if (featureFlags === undefined) {
    featureFlags = _loadFeatureFlags();
  }
  return featureFlags;
}

// This is intended to be run first by the server to write 'feature-flags', and then by the client to read 'feature-flags'
// If we're on the server, we read process.env.
// If we're on the client, we read the JSON from the 'feature-flags' script tag.
function _loadFeatureFlags(): FeatureFlags {
  if (typeof window === 'undefined') {
    // We're on the server, so we can read off of process.env
    return readProcessEnv();
  } else {
    // We're on the client, so we'll read off of the embedded script tag
    const featureFlagsElement = document.getElementById('feature-flags');

    // For some pages (i.e. error pages), the script tag might not be present. In that case, we return an empty object.
    return JSON.parse(featureFlagsElement?.textContent ?? '{}') as FeatureFlags;
  }
}

// This gets executed by the server-side rendering to load feature flag data
export function BootstrapFeatureFlags() {
  return (
    <script id="feature-flags" type="application/json">
      {JSON.stringify(loadFeatureFlags())}
    </script>
  );
}
